import { useEffect, useMemo, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "../../../../ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../../../ui/dialog";
import { Button } from "../../../../ui/button";
import { Input } from "../../../../ui/input";
import { Label } from "../../../../ui/label";
import { Card, CardContent } from "../../../../ui/card";
import { Badge } from "../../../../ui/badge";
import { Skeleton } from "../../../../ui/skeleton";
import { RichEmailEditor } from "..//editor/RichEmailEditor";
import { EmailPreviewIframe } from "../email/EmailPreviewIframe";
import { epicengageSupabasev1 } from "../../../../../integrations/supabase/client";
import { useMerchant } from "../../../../../hooks/useMerchantEngageV1";
import { toast } from "../../../../../hooks/use-toast";
import { Eye, Pencil, Plus, Clock } from "lucide-react";

export type SequenceStep = {
  step: number;
  timing: string;
  defaultSubject: string;
};

export type AutomationSequenceDef = {
  trigger: string;
  name: string;
  steps: SequenceStep[];
};

export const AUTOMATION_SEQUENCES: Record<string, AutomationSequenceDef> = {
  new_customer: {
    trigger: "new_customer",
    name: "New Customer Onboarding",
    steps: [
      { step: 1, timing: "Sent immediately", defaultSubject: "Welcome to {{business_name}}!" },
      { step: 2, timing: "Sent after 3 days", defaultSubject: "Here's what you need to know" },
      { step: 3, timing: "Sent after 7 days", defaultSubject: "A special offer just for you" },
    ],
  },
  post_action: {
    trigger: "post_action",
    name: "Post-Action Follow-up",
    steps: [
      { step: 1, timing: "Sent immediately", defaultSubject: "Thank you for your visit!" },
    ],
  },
  inactive_30d: {
    trigger: "inactive_30d",
    name: "Re-engagement (30 days)",
    steps: [
      { step: 1, timing: "Sent after 30 days inactive", defaultSubject: "We miss you, {{first_name}}" },
      { step: 2, timing: "Sent after 7 more days", defaultSubject: "Last chance — here's something special" },
    ],
  },
};

type TemplateRow = {
  id: string;
  name: string | null;
  subject_line: string | null;
  body_html: string | null;
  cta_text: string | null;
  cta_url: string | null;
};

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  def: AutomationSequenceDef | null;
};

const stepTemplateName = (trigger: string, step: number) => `__automation:${trigger}:${step}`;

export function AutomationSequencePanel({ open, onOpenChange, def }: Props) {
  const { merchant } = useMerchant();
  const [loading, setLoading] = useState(false);
  const [stepTemplates, setStepTemplates] = useState<Record<number, TemplateRow | null>>({});

  const [previewHtml, setPreviewHtml] = useState<string | null>(null);
  const [previewSubject, setPreviewSubject] = useState<string>("");

  const [editorOpen, setEditorOpen] = useState(false);
  const [editorStep, setEditorStep] = useState<SequenceStep | null>(null);
  const [editSubject, setEditSubject] = useState("");
  const [editBody, setEditBody] = useState("");
  const [editCtaText, setEditCtaText] = useState("");
  const [editCtaUrl, setEditCtaUrl] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    if (!merchant || !def) return;
    setLoading(true);
    const names = def.steps.map((s) => stepTemplateName(def.trigger, s.step));
    const { data } = await epicengageSupabasev1
      .from("email_templates")
      .select("id, name, subject_line, body_html, cta_text, cta_url")
      .eq("merchant_id", merchant.id)
      .eq("type", "automation")
      .in("name", names);
    const map: Record<number, TemplateRow | null> = {};
    def.steps.forEach((s) => {
      const key = stepTemplateName(def.trigger, s.step);
      map[s.step] = (data ?? []).find((r) => r.name === key) ?? null;
    });
    setStepTemplates(map);
    setLoading(false);
  };

  useEffect(() => {
    if (open && def) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, def?.trigger, merchant?.id]);

  const openEditor = (step: SequenceStep, existing: TemplateRow | null) => {
    setEditorStep(step);
    setEditingId(existing?.id ?? null);
    setEditSubject(existing?.subject_line ?? step.defaultSubject);
    setEditBody(existing?.body_html ?? "<p>Write your email here…</p>");
    setEditCtaText(existing?.cta_text ?? "");
    setEditCtaUrl(existing?.cta_url ?? "");
    setEditorOpen(true);
  };

  const handleSave = async () => {
    if (!merchant || !def || !editorStep) return;
    if (!editSubject.trim()) {
      toast({ title: "Subject is required", variant: "destructive" });
      return;
    }
    setSaving(true);
    const payload = {
      merchant_id: merchant.id,
      type: "automation",
      name: stepTemplateName(def.trigger, editorStep.step),
      subject_line: editSubject.trim(),
      body_html: editBody,
      cta_text: editCtaText.trim() || null,
      cta_url: editCtaUrl.trim() || null,
      updated_at: new Date().toISOString(),
    };
    let error;
    if (editingId) {
      ({ error } = await epicengageSupabasev1.from("email_templates").update(payload).eq("id", editingId));
    } else {
      ({ error } = await epicengageSupabasev1.from("email_templates").insert(payload));
    }
    setSaving(false);
    if (error) {
      toast({ title: "Failed to save", description: error.message, variant: "destructive" });
      return;
    }
    toast({ title: "Email saved" });
    setEditorOpen(false);
    await load();
  };

  const totalSteps = def?.steps.length ?? 0;

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent side="right" className="w-full sm:max-w-xl overflow-y-auto">
          <SheetHeader>
            <SheetTitle>{def?.name}</SheetTitle>
            <SheetDescription>
              {totalSteps} email{totalSteps === 1 ? "" : "s"} in this automation sequence
            </SheetDescription>
          </SheetHeader>

          <div className="mt-6 space-y-3">
            {loading && (
              <>
                <Skeleton className="h-28 w-full" />
                <Skeleton className="h-28 w-full" />
              </>
            )}
            {!loading &&
              def?.steps.map((s) => {
                const tpl = stepTemplates[s.step] ?? null;
                const exists = !!tpl;
                return (
                  <Card key={s.step}>
                    <CardContent className="p-4">
                      <div className="flex items-center gap-2">
                        <Badge variant="outline">
                          Email {s.step} of {totalSteps}
                        </Badge>
                        <Badge variant={exists ? "default" : "secondary"}>
                          {exists ? "Ready" : "Not created"}
                        </Badge>
                      </div>
                      <h4 className="mt-2 font-medium truncate">
                        {tpl?.subject_line || s.defaultSubject}
                      </h4>
                      <div className="mt-1 flex items-center gap-1.5 text-xs text-muted-foreground">
                        <Clock className="h-3 w-3" />
                        <span>{s.timing}</span>
                      </div>

                      <div className="mt-3 flex gap-2">
                        {exists ? (
                          <>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setPreviewSubject(tpl!.subject_line || s.defaultSubject);
                                setPreviewHtml(tpl!.body_html || "<p><em>Empty email</em></p>");
                              }}
                            >
                              <Eye className="h-4 w-4" /> Preview
                            </Button>
                            <Button size="sm" onClick={() => openEditor(s, tpl)}>
                              <Pencil className="h-4 w-4" /> Edit
                            </Button>
                          </>
                        ) : (
                          <Button size="sm" onClick={() => openEditor(s, null)}>
                            <Plus className="h-4 w-4" /> Create email
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
          </div>
        </SheetContent>
      </Sheet>

      {/* Preview modal */}
      <Dialog open={!!previewHtml} onOpenChange={(v) => !v && setPreviewHtml(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="truncate">{previewSubject}</DialogTitle>
          </DialogHeader>
          <EmailPreviewIframe html={previewHtml ?? ""} minHeight={400} />
        </DialogContent>
      </Dialog>

      {/* Editor modal */}
      <Dialog open={editorOpen} onOpenChange={setEditorOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editorStep ? `Email ${editorStep.step} of ${totalSteps} — ${editorStep.timing}` : ""}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="auto-subject">Subject</Label>
              <Input
                id="auto-subject"
                value={editSubject}
                onChange={(e) => setEditSubject(e.target.value)}
                placeholder="Subject line"
              />
            </div>
            <div>
              <Label>Body</Label>
              <RichEmailEditor value={editBody} onChange={setEditBody} minHeight={260} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="auto-cta-text">CTA text (optional)</Label>
                <Input id="auto-cta-text" value={editCtaText} onChange={(e) => setEditCtaText(e.target.value)} />
              </div>
              <div>
                <Label htmlFor="auto-cta-url">CTA URL (optional)</Label>
                <Input id="auto-cta-url" value={editCtaUrl} onChange={(e) => setEditCtaUrl(e.target.value)} />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditorOpen(false)} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? "Saving…" : "Save email"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
